import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import {
  supportChat, createSupportTicket, listMyTickets, getTicket, replyTicket,
} from "@/lib/support.functions";
import { supabase } from "@/integrations/supabase/client";
import { useLocaleFmt } from "@/lib/i18n/format";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AiMarkdown } from "@/components/app/ai-markdown";
import { Bot, Send, Loader2, LifeBuoy, Ticket, MessageCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/supporto")({
  component: SupportPage,
});

type Ticket = {
  id: string; subject: string; category: string; priority: string; status: string;
  created_at: string; last_reply_at: string | null;
};

function SupportPage() {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader
        title={t("pages.supporto.title")}
        description={t("pages.supporto.description")}
        actions={<Badge variant="secondary" className="gap-1"><LifeBuoy className="h-3 w-3" /> 24/7</Badge>}
      />
      <PageBody>
        <Tabs defaultValue="chatbot">
          <TabsList>
            <TabsTrigger value="chatbot"><Bot className="mr-2 h-4 w-4" />{t("pages.supporto.tabChatbot")}</TabsTrigger>
            <TabsTrigger value="faq"><MessageCircle className="mr-2 h-4 w-4" />{t("pages.supporto.tabFaq")}</TabsTrigger>
            <TabsTrigger value="tickets"><Ticket className="mr-2 h-4 w-4" />{t("pages.supporto.tabMyTickets")}</TabsTrigger>
            <TabsTrigger value="new">{t("pages.supporto.tabNew")}</TabsTrigger>
          </TabsList>

          <TabsContent value="chatbot"><ChatbotPanel /></TabsContent>
          <TabsContent value="faq"><FaqPanel /></TabsContent>
          <TabsContent value="tickets"><TicketsPanel /></TabsContent>
          <TabsContent value="new"><NewTicketPanel /></TabsContent>
        </Tabs>
      </PageBody>
    </>
  );
}

function ChatbotPanel() {
  const { t } = useTranslation();
  const ask = useServerFn(supportChat);
  const [messages, setMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([
    { role: "assistant", content: t("pages.supporto.assistantHello") },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, busy]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    try {
      const history = messages.slice(-10);
      const res: any = await ask({ data: { message: msg, history } });
      if (!res?.ok) {
        toast.error(res?.error ?? t("pages.supporto.sendError"));
        setMessages((m) => [...m, { role: "assistant", content: t("pages.supporto.assistantFallback") }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", content: res.content || "..." }]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Bot className="h-5 w-5" />{t("pages.supporto.assistantTitle")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div ref={scrollRef} className="mb-4 max-h-[400px] min-h-[300px] overflow-y-auto rounded-lg border border-border bg-secondary/30 p-4 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "ml-auto max-w-[80%] rounded-xl bg-primary/15 px-3 py-2 text-sm" : "mr-auto max-w-[90%] rounded-xl bg-background px-3 py-2 text-sm border border-border"}>
              {m.role === "assistant" ? <AiMarkdown>{m.content}</AiMarkdown> : <div className="whitespace-pre-wrap">{m.content}</div>}
            </div>
          ))}
          {busy && <div className="mr-auto inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />{t("pages.supporto.thinking")}</div>}
        </div>
        <div className="flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder={t("pages.supporto.askPh")} disabled={busy} />
          <Button onClick={send} disabled={!input.trim() || busy}><Send className="h-4 w-4" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FaqPanel() {
  const { t } = useTranslation();
  const faqs = (t("pages.supporto.faq", { returnObjects: true }) as { q: string; a: string }[]) ?? [];
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {faqs.map((f, i) => (
        <Card key={i}>
          <CardHeader><CardTitle className="text-base">{f.q}</CardTitle></CardHeader>
          <CardContent className="text-sm text-muted-foreground"><AiMarkdown>{f.a}</AiMarkdown></CardContent>
        </Card>
      ))}
    </div>
  );
}

function TicketsPanel() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const list = useServerFn(listMyTickets);
  const [rows, setRows] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const r: any = await list();
    setRows(Array.isArray(r) ? r : []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (cancelled || !u.user) return;
      channel = supabase
        .channel(`support-tickets-list-${u.user.id}`)
        .on("postgres_changes", { event: "*", schema: "public", table: "support_tickets" }, () => load())
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "support_ticket_messages" }, () => load())
        .subscribe();
    })();
    return () => { cancelled = true; if (channel) supabase.removeChannel(channel); };
  }, []);

  if (openId) return <TicketDetail id={openId} onBack={() => { setOpenId(null); load(); }} />;

  if (loading) return <p className="text-sm text-muted-foreground">{t("pages.supporto.loading")}</p>;
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{t("pages.supporto.noTickets")}</p>;
  return (
    <div className="space-y-2">
      {rows.map((tk) => (
        <button key={tk.id} onClick={() => setOpenId(tk.id)} className="block w-full rounded-lg border border-border bg-card p-4 text-left hover:bg-secondary/30">
          <div className="flex items-center justify-between gap-3">
            <div className="font-medium">{tk.subject}</div>
            <Badge variant={tk.status === "open" ? "default" : "secondary"}>{tk.status}</Badge>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">{tk.category} · {tk.priority} · {new Date(tk.created_at).toLocaleDateString(localeTag)}</div>
        </button>
      ))}
    </div>
  );
}

function TicketDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const get = useServerFn(getTicket);
  const reply = useServerFn(replyTicket);
  const [data, setData] = useState<any>(null);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r: any = await get({ data: { id } });
    setData(r);
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    const channel = supabase
      .channel(`support-ticket-${id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "support_ticket_messages", filter: `ticket_id=eq.${id}` }, () => {
        load();
        toast.info(t("pages.supporto.newReply"));
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "support_tickets", filter: `id=eq.${id}` }, () => {
        load();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [id]);

  const send = async () => {
    if (!content.trim()) return;
    setBusy(true);
    const r: any = await reply({ data: { ticketId: id, content } });
    if (!r?.ok) toast.error(r?.error ?? t("pages.supporto.sendError"));
    else { setContent(""); await load(); }
    setBusy(false);
  };

  if (!data?.ticket) return <p className="text-sm text-muted-foreground">{t("pages.supporto.loading")}</p>;
  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" />{t("pages.supporto.backToTickets")}</Button>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-3">
            <span>{data.ticket.subject}</span>
            <Badge>{data.ticket.status}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(data.messages ?? []).map((m: any) => (
              <div key={m.id} className={m.role === "user" ? "ml-auto max-w-[80%] rounded-xl bg-primary/15 px-3 py-2 text-sm" : "mr-auto max-w-[90%] rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm"}>
                <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{m.role === "admin" ? t("pages.supporto.roleSupport") : m.role === "bot" ? t("pages.supporto.roleBot") : t("pages.supporto.roleYou")} · {new Date(m.created_at).toLocaleString(localeTag)}</div>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2">
            <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={t("pages.supporto.replyPh")} rows={3} />
            <div className="flex justify-end">
              <Button onClick={send} disabled={!content.trim() || busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("pages.supporto.sendReply")}</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function NewTicketPanel() {
  const { t } = useTranslation();
  const create = useServerFn(createSupportTicket);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState<"billing" | "technical" | "feature_request" | "account" | "other">("other");
  const [priority, setPriority] = useState<"low" | "normal" | "high">("normal");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!subject.trim() || !body.trim()) return;
    setBusy(true);
    const r: any = await create({ data: { subject, body, category, priority } });
    if (!r?.ok) toast.error(r?.error ?? t("pages.supporto.sendError"));
    else {
      toast.success(t("pages.supporto.ticketCreated"));
      setSubject(""); setBody(""); setCategory("other"); setPriority("normal");
    }
    setBusy(false);
  };

  return (
    <Card>
      <CardHeader><CardTitle>{t("pages.supporto.newTicket")}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-medium">{t("pages.supporto.subject")}</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("pages.supporto.subjectPh")} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium">{t("pages.supporto.category")}</label>
            <Select value={category} onValueChange={(v: any) => setCategory(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="billing">{t("pages.supporto.catBilling")}</SelectItem>
                <SelectItem value="technical">{t("pages.supporto.catTechnical")}</SelectItem>
                <SelectItem value="feature_request">{t("pages.supporto.catFeature")}</SelectItem>
                <SelectItem value="account">{t("pages.supporto.catAccount")}</SelectItem>
                <SelectItem value="other">{t("pages.supporto.catOther")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">{t("pages.supporto.priority")}</label>
            <Select value={priority} onValueChange={(v: any) => setPriority(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">{t("pages.supporto.prioLow")}</SelectItem>
                <SelectItem value="normal">{t("pages.supporto.prioNormal")}</SelectItem>
                <SelectItem value="high">{t("pages.supporto.prioHigh")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">{t("pages.supporto.description2")}</label>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder={t("pages.supporto.bodyPh")} />
        </div>
        <div className="flex justify-end">
          <Button onClick={submit} disabled={!subject.trim() || !body.trim() || busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("pages.supporto.sendTicket")}</Button>
        </div>
        <p className="text-xs text-muted-foreground">{t("pages.supporto.paymentNotice")} <Link to="/app/billing" className="underline">{t("pages.supporto.billingLink")}</Link>.</p>
      </CardContent>
    </Card>
  );
}
